'use client';

import { useTranslations } from 'next-intl';

import { HitsPerPageSelector } from './HitsPerPageSelector';
import { PaginationControls } from './PaginationControls';

/**
 * Props pour PaginationWidget
 */
interface PaginationWidgetProps {
  /**
   * Nombre de pages à afficher autour de la page courante
   * @default 2 - Affiche 2 pages avant et 2 pages après la page courante
   */
  padding?: number;
  /**
   * Callback appelé lors du changement de page
   * Utile pour scroller vers le haut des résultats
   */
  onPageChange?: () => void;
  /**
   * Classe CSS additionnelle
   */
  className?: string;
  disableItemsPerPage?: boolean;
}

/**
 * Composant PaginationWidget - Wrapper complet pour la pagination
 *
 * Principe SOLID: Single Responsibility
 * - Responsable uniquement de l'orchestration des composants de pagination
 * - Délègue l'affichage à PaginationControls
 * - Délègue le sélecteur à HitsPerPageSelector
 *
 * Principe SoC:
 * - Sépare la mise en page de la logique
 * - Utilise des composants spécialisés pour chaque partie
 *
 * Principe DRY:
 * - Réutilise les composants enfants
 * - Pas de duplication avec l'ancien code
 *
 * Bonnes pratiques React:
 * - Composition de composants
 * - Props drilling minimal
 *
 * Layout:
 * - Desktop: PaginationControls centré, HitsPerPageSelector à droite
 * - Mobile: Empilé verticalement
 * - Match le layout de l'ancien moteur
 */
export function PaginationWidget({
  padding = 2,
  onPageChange,
  className = '',
  disableItemsPerPage = false,
}: PaginationWidgetProps) {
  const t = useTranslations('Blocs.PaginationRecherche');

  return (
    <div
      data-role="pagination"
      className={`mt-16 flex flex-col items-center justify-between gap-3 sm:flex-row ${className}`}
    >
      {/* Spacer pour centrer la pagination sur desktop */}
      <div />

      {/* Contrôles de pagination */}
      <PaginationControls
        padding={padding}
        onPageChange={onPageChange}
        className="px-4 sm:px-0"
      />

      {/* Sélecteur de hits par page */}
      <div className="flex flex-row items-center gap-2 whitespace-nowrap">
        {!disableItemsPerPage && (
          <span className="shrink-0 text-sm text-gray-600">
            {t('oeuvres_par_pages')}
          </span>
        )}
        <HitsPerPageSelector hideUi={disableItemsPerPage} />
      </div>
    </div>
  );
}
